/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { useMemo } from 'react';
import { SORT_DIRECTION, COLUMN_CONTENT_JUSTIFY } from '@veeam/components';
import { gridHeaderCell } from '@veeam/components/lib/GridCore/renderers/gridHeaderCell';
import { gridNormalCell } from '@veeam/components/lib/GridCore/renderers/gridNormalCell';

import type { GridColumnConfig } from '@veeam/components';

import { GridColumnJustify, GridSortDirections } from '../enums';

import type { GridStoreImpl as GridStore } from '../stores';

export const useColumns = <RowDataT, TotalRowDataT, FilterT>(store: GridStore<RowDataT, TotalRowDataT, FilterT>) => useMemo(
    () => store.columns
        .filter(column => !column.hidden)
        .map((column) => {
            const sorting = store.sortingStore.sortingConfig?.direction === GridSortDirections.Asc ? SORT_DIRECTION.asc : SORT_DIRECTION.desc;
            const justify = column.justify === GridColumnJustify.Right ? COLUMN_CONTENT_JUSTIFY.end : COLUMN_CONTENT_JUSTIFY.start;

            return {
                id: column.id,
                width: column.width,
                flex: column.flex,
                minWidth: column.minWidth,
                maxWidth: column.maxWidth,
                header: gridHeaderCell({
                    title: column.title,
                    sorting: store.sortingStore.sortingConfig?.key === column.id ? sorting : null,
                    onClick: () => store.sortingStore.onHeaderClick(column),
                    clickable: column.isSortable !== false,
                    highlighted: column.highlighted,
                }),
                cell: gridNormalCell({
                    value: column.cell,
                }),
                justify,
                order: column.order,
                highlighted: column.highlighted,
            } as GridColumnConfig<RowDataT>;
        }),
    [
        store.sortingStore.sortingConfig?.key,
        store.sortingStore.sortingConfig?.direction,
        store.columns.map(column => column.order).join(),
        store.columns.map(column => column.width).join(),
        store.columns.map(column => column.hidden).join(),
    ]
);
